import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Timer as TimerIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  Receipt as ReceiptIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  Send as SendIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { zeiterfassungApi, ticketsApi, adressenApi } from '../services/api';
import { projectService } from '../services/projectService';
import type {
  Zeiterfassung,
  ZeiterfassungStatistik,
  CreateZeiterfassungDto,
  UpdateZeiterfassungDto,
  Ticket,
  Adresse,
} from '../types/api';
import type { Project } from '../types/project';

type FilterMode = 'alle' | 'heute' | 'dieseWoche' | 'diesesMonat';

const ZeiterfassungPage: React.FC = () => {
  const [zeiterfassungen, setZeiterfassungen] = useState<Zeiterfassung[]>([]);
  const [statistik, setStatistik] = useState<ZeiterfassungStatistik | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('dieseWoche');
  const [statusFilter, setStatusFilter] = useState<string>('alle');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Zeiterfassung | null>(null);

  // Form Data
  const [formData, setFormData] = useState<CreateZeiterfassungDto>({
    datum: new Date().toISOString().split('T')[0],
    taetigkeit: '',
    abrechenbar: true,
  });

  // Lookup Data
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [adressen, setAdressen] = useState<Adresse[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    loadData();
    loadLookupData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [zeitRes, statRes] = await Promise.all([
        zeiterfassungApi.getAll({ take: 500 }),
        zeiterfassungApi.getStatistik(),
      ]);

      setZeiterfassungen(zeitRes.data);
      setStatistik(statRes.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Zeiterfassungen');
    } finally {
      setLoading(false);
    }
  };

  const loadLookupData = async () => {
    try {
      const [ticketRes, adressenRes, projectList] = await Promise.all([
        ticketsApi.getAll({ status: 'Offen', take: 100 }),
        adressenApi.getAll(),
        projectService.getAll(),
      ]);
      setTickets(ticketRes.data);
      // Nur Kunden (Mandanten) anzeigen, keine internen Adressen
      setAdressen(adressenRes.data.filter((a) => a.istMandant));
      setProjects(projectList);
    } catch (err) {
      console.error('Fehler beim Laden der Lookup-Daten:', err);
    }
  };

  const filteredData = useMemo(() => {
    let result = [...zeiterfassungen];

    // Textsuche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (z) =>
          z.taetigkeit.toLowerCase().includes(term) ||
          (z.ticketTitel || '').toLowerCase().includes(term) ||
          (z.adresseName || '').toLowerCase().includes(term) ||
          (z.projectName || '').toLowerCase().includes(term)
      );
    }

    // Status-Filter
    if (statusFilter !== 'alle') {
      result = result.filter((z) => z.status === statusFilter);
    }

    // Zeitraum-Filter
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1);

    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    switch (filterMode) {
      case 'heute':
        result = result.filter((z) => {
          const d = new Date(z.datum);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === today.getTime();
        });
        break;
      case 'dieseWoche':
        result = result.filter((z) => new Date(z.datum) >= startOfWeek);
        break;
      case 'diesesMonat':
        result = result.filter((z) => new Date(z.datum) >= startOfMonth);
        break;
    }

    // Nach Datum sortieren (neueste zuerst)
    result.sort((a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime());

    return result;
  }, [zeiterfassungen, searchTerm, filterMode, statusFilter]);

  const handleOpenDialog = (item?: Zeiterfassung) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        datum: item.datum.split('T')[0],
        startZeit: item.startZeit || '',
        endZeit: item.endZeit || '',
        dauerStunden: item.dauerStunden,
        taetigkeit: item.taetigkeit,
        notizen: item.notizen || '',
        ticketID: item.ticketID,
        projectID: item.projectID,
        adresseID: item.adresseID,
        abrechenbar: item.abrechenbar,
      });
    } else {
      setEditingItem(null);
      setFormData({
        datum: new Date().toISOString().split('T')[0],
        taetigkeit: '',
        abrechenbar: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSave = async () => {
    try {
      if (editingItem) {
        await zeiterfassungApi.update(editingItem.zeiterfassungID, formData as UpdateZeiterfassungDto);
      } else {
        await zeiterfassungApi.create(formData);
      }
      handleCloseDialog();
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert(err.response?.data?.message || 'Fehler beim Speichern');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      await zeiterfassungApi.delete(id);
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const handleFreigeben = async () => {
    if (selectedIds.length === 0) {
      alert('Bitte wählen Sie mindestens einen Eintrag aus');
      return;
    }
    try {
      await zeiterfassungApi.freigeben(selectedIds);
      setSelectedIds([]);
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Freigeben:', err);
      alert(err.response?.data?.message || 'Fehler beim Freigeben');
    }
  };

  const handleZuruecksetzen = async (id: number) => {
    try {
      await zeiterfassungApi.zuruecksetzen(id);
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Zurücksetzen:', err);
      alert(err.response?.data?.message || 'Fehler beim Zurücksetzen');
    }
  };

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const entwuerfe = filteredData.filter((z) => z.status === 'Entwurf');
    if (selectedIds.length === entwuerfe.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(entwuerfe.map((z) => z.zeiterfassungID));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Entwurf':
        return 'default';
      case 'Freigegeben':
        return 'primary';
      case 'Abgerechnet':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDauer = (stunden: number) => {
    const h = Math.floor(stunden);
    const m = Math.round((stunden - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')} h`;
  };

  const formatBetrag = (betrag?: number) => {
    if (!betrag) return '-';
    return `${betrag.toFixed(2)} CHF`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TimerIcon /> Zeiterfassung
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>
            Aktualisieren
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Neuer Eintrag
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Statistik-Karten */}
      {statistik && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TodayIcon color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Heute
                    </Typography>
                    <Typography variant="h5">{formatDauer(statistik.stundenHeute)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WeekIcon color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Diese Woche
                    </Typography>
                    <Typography variant="h5">{formatDauer(statistik.stundenDieseWoche)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MonthIcon color="primary" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Dieser Monat
                    </Typography>
                    <Typography variant="h5">{formatDauer(statistik.stundenDieserMonat)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon color="success" />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Abrechenbar (Monat)
                    </Typography>
                    <Typography variant="h5">{formatDauer(statistik.abrechenbareStundenDieserMonat)}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Zeitraum</InputLabel>
              <Select
                value={filterMode}
                label="Zeitraum"
                onChange={(e) => setFilterMode(e.target.value as FilterMode)}
              >
                <MenuItem value="alle">Alle</MenuItem>
                <MenuItem value="heute">Heute</MenuItem>
                <MenuItem value="dieseWoche">Diese Woche</MenuItem>
                <MenuItem value="diesesMonat">Dieser Monat</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="alle">Alle</MenuItem>
                <MenuItem value="Entwurf">Entwurf</MenuItem>
                <MenuItem value="Freigegeben">Freigegeben</MenuItem>
                <MenuItem value="Abgerechnet">Abgerechnet</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
            {selectedIds.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SendIcon />}
                onClick={handleFreigeben}
              >
                {selectedIds.length} Einträge freigeben
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Tabelle */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < filteredData.filter((z) => z.status === 'Entwurf').length
                  }
                  checked={
                    filteredData.filter((z) => z.status === 'Entwurf').length > 0 &&
                    selectedIds.length === filteredData.filter((z) => z.status === 'Entwurf').length
                  }
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Tätigkeit</TableCell>
              <TableCell>Ticket / Projekt / Kunde</TableCell>
              <TableCell align="right">Dauer</TableCell>
              <TableCell align="right">Betrag</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Keine Einträge gefunden
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((item) => (
                <TableRow key={item.zeiterfassungID} hover>
                  <TableCell padding="checkbox">
                    {item.status === 'Entwurf' && (
                      <Checkbox
                        checked={selectedIds.includes(item.zeiterfassungID)}
                        onChange={() => toggleSelection(item.zeiterfassungID)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(item.datum).toLocaleDateString('de-DE')}
                    {item.startZeit && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.startZeit} - {item.endZeit}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {item.taetigkeit}
                    </Typography>
                    {!item.abrechenbar && (
                      <Chip label="Nicht abrechenbar" size="small" color="warning" sx={{ mt: 0.5 }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {item.ticketTitel && (
                      <Typography variant="body2" color="primary">
                        {item.ticketTitel}
                      </Typography>
                    )}
                    {item.projectName && (
                      <Typography variant="caption" display="block">
                        {item.projectName}
                      </Typography>
                    )}
                    {item.adresseName && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {item.adresseName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatDauer(item.dauerStunden)}</TableCell>
                  <TableCell align="right">{formatBetrag(item.betrag)}</TableCell>
                  <TableCell>
                    <Chip label={item.status} size="small" color={getStatusColor(item.status)} />
                  </TableCell>
                  <TableCell align="right">
                    {item.status === 'Entwurf' && (
                      <>
                        <Tooltip title="Bearbeiten">
                          <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Löschen">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(item.zeiterfassungID)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {item.status === 'Freigegeben' && (
                      <Tooltip title="Zurücksetzen">
                        <IconButton
                          size="small"
                          onClick={() => handleZuruecksetzen(item.zeiterfassungID)}
                        >
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Datum"
                value={formData.datum}
                onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="time"
                label="Start"
                value={formData.startZeit || ''}
                onChange={(e) => setFormData({ ...formData, startZeit: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="time"
                label="Ende"
                value={formData.endZeit || ''}
                onChange={(e) => setFormData({ ...formData, endZeit: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Dauer (Stunden)"
                value={formData.dauerStunden || ''}
                onChange={(e) => setFormData({ ...formData, dauerStunden: parseFloat(e.target.value) || undefined })}
                inputProps={{ step: 0.25, min: 0 }}
                helperText="Alternativ zu Start/Ende direkt eingeben"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Tätigkeit"
                value={formData.taetigkeit}
                onChange={(e) => setFormData({ ...formData, taetigkeit: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={tickets}
                getOptionLabel={(option) => option.titel}
                value={tickets.find((t) => t.ticketID === formData.ticketID) || null}
                onChange={(_, newValue) => setFormData({ ...formData, ticketID: newValue?.ticketID })}
                renderInput={(params) => <TextField {...params} label="Ticket (optional)" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={projects}
                getOptionLabel={(option) => option.name}
                value={projects.find((p) => p.projectID === formData.projectID) || null}
                onChange={(_, newValue) => setFormData({ ...formData, projectID: newValue?.projectID })}
                renderInput={(params) => <TextField {...params} label="Projekt (optional)" />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={adressen}
                getOptionLabel={(option) => {
                  if (option.firma) return option.firma;
                  const name = [option.vorname, option.nachname].filter(Boolean).join(' ');
                  return name || `Adresse ${option.adresseID}`;
                }}
                value={adressen.find((a) => a.adresseID === formData.adresseID) || null}
                onChange={(_, newValue) => setFormData({ ...formData, adresseID: newValue?.adresseID })}
                renderInput={(params) => <TextField {...params} label="Kunde/Mandant (optional)" />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notizen"
                value={formData.notizen || ''}
                onChange={(e) => setFormData({ ...formData, notizen: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.abrechenbar ?? true}
                    onChange={(e) => setFormData({ ...formData, abrechenbar: e.target.checked })}
                  />
                }
                label="Abrechenbar"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.taetigkeit || (!formData.dauerStunden && !formData.startZeit)}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ZeiterfassungPage;
